/**
 * Tờ nhân viên của CHÍNH MÌNH (v0.22.3 B3/B4/B6/B7, ADR-122; chủ dự án Q3 = A, Q4 = có) — việc thuần, tách khỏi giao diện để thử.
 *
 * Máy chủ là chốt (400 khi đặt mã hộ cho mình, `canMa` khi đổi số của mình thiếu mã); đây chỉ là để giao diện không đưa nút luôn bị từ chối.
 */
import { chuanHoaSdtVn } from '@sapa/shared'
import { ApiFailure } from './api.ts'

export interface ViecTrenTo {
  /** Tờ đang mở là của chính người đăng nhập. */
  laToi: boolean
  /** Nút "Cho nghỉ việc" (máy chủ luôn từ chối tự nghỉ việc). */
  choNghiViec: boolean
  /** Chip đổi vai bị khoá, kèm câu giải thích (máy chủ luôn từ chối tự đổi vai). */
  khoaVai: boolean
  /** "Đặt mã đăng nhập" hộ (người khác). Với chính mình là lối vượt bước gõ mã cũ → ẩn. */
  datMaHo: boolean
  /** "Đổi mã của tôi": cần mã đang dùng, giữ phiên đang dùng (`POST /v1/auth/doi-ma`). */
  doiMaCuaToi: boolean
}

export function viecTrenTo(nvId: string | null, toiId: string): ViecTrenTo {
  const laToi = nvId !== null && nvId === toiId
  return {
    laToi,
    choNghiViec: !laToi,
    khoaVai: laToi,
    datMaHo: !laToi,
    doiMaCuaToi: laToi,
  }
}

/**
 * Đổi SỐ ĐĂNG NHẬP của chính mình sang số khác phải gõ lại mã (Q4). Chỉ đòi khi: là mình, đang có số đăng nhập, số nhập hợp lệ và
 * (sau chuẩn hoá) KHÁC số hiện tại. Sửa tên / vai / giữ số thì không đòi.
 */
export function canMaKhiDoiSo(x: {
  laToi: boolean
  /**
   * V2-V1 / V2-N3(a): tờ của MỘT CHỦ KHÁC. Luật khác tờ của mình: số "hiện tại" là số trên hồ sơ (chủ chưa có mã đăng nhập vẫn có thể
   * có số), và XOÁ TRẮNG ô số cũng là đổi số (hồ sơ đang có số) — máy chủ (`viecKhiLuu`) hỏi mã cả hai.
   */
  laChuKhac?: boolean
  sdtNhap: string
  sdtHienTai: string | null
}): boolean {
  if (x.laChuKhac === true) {
    const cuChuKhac = x.sdtHienTai === null ? null : chuanHoaSdtVn(x.sdtHienTai)
    if (x.sdtNhap.trim() === '') return x.sdtHienTai !== null
    const moiChuKhac = chuanHoaSdtVn(x.sdtNhap.trim())
    return moiChuKhac !== null && moiChuKhac !== cuChuKhac
  }
  if (!x.laToi || x.sdtHienTai === null) return false
  const moi = chuanHoaSdtVn(x.sdtNhap.trim())
  const cu = chuanHoaSdtVn(x.sdtHienTai)
  return moi !== null && cu !== null && moi !== cu
}

/**
 * v0.23.1 (ADR-123; B-N1): ô "Mã đăng nhập của bạn" khi bấm Lưu — CÙNG LUẬT với `viecCanMa` nhánh `luu` của máy chủ
 * (`apps/api/src/domain/auth/viec-cham-chu.ts`; máy chủ vẫn là chốt, đây để không bắt bấm Lưu hai lần):
 *   · thêm người MỚI / nâng người có sẵn lên Chủ tiệm (chip "Chủ tiệm");
 *   · bỏ chip "Chủ tiệm" của một chủ khác;
 *   · đổi SỐ ĐĂNG NHẬP của chính mình (B4);
 *   · đổi hoặc XOÁ TRẮNG số của một chủ khác — kể cả chủ CHƯA có mã đăng nhập (B-V1, V2-V1: máy chủ so với số trên hồ sơ);
 *     hồ sơ chưa có số mà gõ số mới cũng hỏi (V2-N3(a)).
 * Sửa tên, đổi giữa quản lý / lễ tân / kỹ thuật viên, đổi số của người không phải chủ: không hỏi.
 */
export function canMaKhiLuu(x: {
  laToi: boolean
  laMoi: boolean
  vaiCu: string | null
  vaiChon: string
  sdtNhap: string
  sdtHienTai: string | null
}): boolean {
  const laChuCu = !x.laMoi && x.vaiCu === 'owner'
  if (!laChuCu && x.vaiChon === 'owner') return true
  if (laChuCu && x.vaiChon !== 'owner') return true
  // B-V1 / V2-V1: đổi hoặc xoá trắng số của một chủ khác hỏi mã dù chủ đó chưa có mã đăng nhập (máy chủ so với số trên hồ sơ).
  if (x.laToi) return canMaKhiDoiSo({ laToi: true, sdtNhap: x.sdtNhap, sdtHienTai: x.sdtHienTai })
  return (
    laChuCu &&
    canMaKhiDoiSo({
      laToi: false,
      laChuKhac: true,
      sdtNhap: x.sdtNhap,
      sdtHienTai: x.sdtHienTai,
    })
  )
}

/**
 * G-L1: dòng gợi ý dưới nhãn của ô mã khi Lưu — nói đúng việc đang xin mã. `doi-so-cua-toi` (B4) · `doi-so-chu` (chỉ đổi SỐ của một chủ khác,
 * vai vẫn Chủ tiệm) · `quyen-chu` (giao / bỏ vai Chủ tiệm). Cùng bảng với `viecCanMa` của máy chủ.
 */
export function viecOMa(x: {
  laToi: boolean
  laMoi: boolean
  vaiCu: string | null
  vaiChon: string
  sdtNhap: string
  sdtHienTai: string | null
}): 'doi-so-cua-toi' | 'doi-so-chu' | 'quyen-chu' {
  const laChuCu = !x.laMoi && x.vaiCu === 'owner'
  const doiVaiChu = (!laChuCu && x.vaiChon === 'owner') || (laChuCu && x.vaiChon !== 'owner')
  if (doiVaiChu) return 'quyen-chu'
  if (x.laToi) return 'doi-so-cua-toi'
  return 'doi-so-chu'
}

/**
 * B-L2: máy chủ đòi mã theo SỐ ĐĂNG NHẬP (`staff_login.phone`), web đoán theo số trên hồ sơ (`staff.phone`) — hai số có thể lệch.
 * Máy chủ trả `canMa` thì ô mã PHẢI hiện dù web thấy số không đổi, kẻo chủ tiệm bị 400 mà không có chỗ gõ mã.
 */
export function mayChuDoiMa(e: unknown): boolean {
  return e instanceof ApiFailure && e.canMa
}

export function hienOMaXacNhan(x: { canMaTinh: boolean; mayChuDoi: boolean }): boolean {
  return x.canMaTinh || x.mayChuDoi
}
